import i18n from 'i18next';
import {SetStateAction} from 'react';
import {IElement} from '../incase-domain/elements';
import {ELEMENT, interactiveWithTryButton} from '../incase-domain/elements/base/ElementTypes';
import {IElementQuestionnaire} from '../incase-domain/elements/base/questionElementBase';
import {ISlideOption} from '../incase-domain/slide';

export function getElementClickBeforeDisplayOptions(element: IElement): ISlideOption[] {
    const options: ISlideOption[] = [];

    if (element.type === ELEMENT.QUESTION) {
        element.answers.forEach((answer, answerIndex) => {
            options.push({label: answer.text || `${element.name || element.id}-${answerIndex}`, value: `${element.id}-${answerIndex}`});
        });
    }

    const currentElOption = {label: getElementName(element), value: element.id};

    if (interactiveWithTryButton.includes(element.type)) {
        if ((element as IElementQuestionnaire).haveTryButton) options.push(currentElOption);
    } else {
        options.push(currentElOption);
    }

    return options;
}

export function getElementName(element: IElement): string {
    switch (element.type) {
        case ELEMENT.STATIC_TEXT:
            return `${element.name || element.text || element.id}`;
        case ELEMENT.HTML:
            return `${element.name || element.text || element.id}`;
        case ELEMENT.STATIC_LIST:
            return `${element.items || element.id}`;
        case ELEMENT.STATIC_TABLE:
        case ELEMENT.STATIC_TABLE_V2:
            return `${element.name || element.text || element.id}`;
        case ELEMENT.IMAGE:
            return `${element.name || element.title || element.id}`;
        case ELEMENT.VIDEO:
            return `${element.name || element.text || element.id}`;
        case ELEMENT.QUESTION:
            return `${element.question || element.id}`;
        case ELEMENT.YES_NO_QUESTION:
        case ELEMENT.YES_NO_QUESTION_V2:
            return `${element.name || element.question || element.id}`;
        case ELEMENT.SEQUENCE_QUESTION:
            return `${element.question || element.id}`;
        case ELEMENT.MULTI_SELECT:
            return `${element.name || element.text || element.id}`;
        case ELEMENT.BUTTON:
            return `${element.name || element.text || element.id}`;
        case ELEMENT.STATUS:
            return `${element.name || element.id}`;
        case ELEMENT.RECTANGLE:
            return `${element.name}`;
        case ELEMENT.ELLIPSE:
            return `${element.name}`;
        case ELEMENT.STAR:
            return `${element.name}`;
        case ELEMENT.LINE:
            return `${element.name}`;
        case ELEMENT.LINE_V2:
            return `${element.name}`;
        case ELEMENT.TRIANGLE:
            return `${element.name}`;
        case ELEMENT.ARROW:
            return `${element.name}`;
        case ELEMENT.ARROW_V2:
            return `${element.name}`;
        case ELEMENT.TEXT_INPUT:
            return `${element.name || element.label || element.id}`;
        case ELEMENT.MODAL:
            return `${element.name || element.title || element.id}`;
        case ELEMENT.FLIP_CARD:
            return `${element.name}`;
        case ELEMENT.FORM:
            return `${element.name || element.title}`;
        default:
            return 'Unknown';
    }
}

export const getElementDefaultName = (type: ELEMENT): string => {
    switch (type) {
        case ELEMENT.IMAGE:
            return 'Image';
        case ELEMENT.VIDEO:
            return 'Video';
        case ELEMENT.STATIC_TEXT:
            return 'Static text';
        case ELEMENT.HTML:
            return 'Html';
        case ELEMENT.STATIC_LIST:
            return 'Static list';
        case ELEMENT.STATIC_TABLE:
        case ELEMENT.STATIC_TABLE_V2:
            return 'Static table';
        case ELEMENT.YES_NO_QUESTION:
        case ELEMENT.YES_NO_QUESTION_V2:
            return 'Yes No question';
        case ELEMENT.IMAGE_GALLERY:
            return 'Image gallery';
        case ELEMENT.QUESTION:
            return 'Question';
        case ELEMENT.SEQUENCE_QUESTION:
            return 'Sequence question';
        case ELEMENT.MULTI_SELECT:
            return 'Multiselect';
        case ELEMENT.QUESTION_REPORT:
            return 'Question report';
        case ELEMENT.RECTANGLE:
            return 'Rectangle';
        case ELEMENT.ELLIPSE:
            return 'Ellipse';
        case ELEMENT.BUTTON:
            return 'Button';
        case ELEMENT.LINE:
            return 'Line';
        case ELEMENT.LINE_V2:
            return 'LineV2';
        case ELEMENT.TRIANGLE:
            return 'Triangle';
        case ELEMENT.STAR:
            return 'Star';
        case ELEMENT.ARROW:
            return 'Arrow';
        case ELEMENT.ARROW_V2:
            return 'Arrow';
        case ELEMENT.STATUS:
            return 'Status';
        case ELEMENT.TEXT_INPUT:
            return 'Text input';
        case ELEMENT.FORM:
            return 'Form element';
        case ELEMENT.MODAL:
            return 'Modal element';
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            return 'Photo answer question';
        case ELEMENT.FLIP_CARD:
            return 'Flip card';
        case ELEMENT.MATCH_PAIR_QUESTION:
            return 'Match pair question';
        case ELEMENT.IMAGE_MARKUP:
            return 'Image markup question';
        case ELEMENT.LIKERT_SCALE:
            return 'Likert scale';
        default:
            return `${i18n.t('designSidepanel.elementName')}`;
    }
};

export const snapHandler = (
    points: Record<string, {x: number; y: number}[]>,
    x: number,
    y: number,
    width: number,
    height: number,
    clientWidth: number,
    clientHeight: number,
    t: number,
    event: Interact.DragEvent,
    index: number,
    setter: React.Dispatch<SetStateAction<({x1: number | null; x2: number | null; y1: number | null; y2: number | null} | never)[]>>
): {x: number | null; y: number | null} => {
    const res: {x: number | null; y: number | null} = {x: null, y: null};
    const xNew = (x / clientWidth) * 100;
    const yNew = (y / clientHeight) * 100;
    let treshold = t;
    const locked: {x: number | null; y: number | null} = {x: null, y: null};
    setter([]);
    if (event.type === 'dragmove' || (event.type === 'dragend' && (event.ctrlKey || event.altKey))) {
        const temp: {x1: number | null; x2: number | null; y1: number | null; y2: number | null}[] = [];
        Object.entries(points).forEach(([key, val]) => {
            if (!(+key === index) && event.altKey) {
                val.forEach((point: {x: number; y: number}) => {
                    // LEFT
                    if (point.x - treshold <= xNew && xNew <= point.x + treshold) {
                        res.x = (point.x * clientWidth) / 100;
                        locked.x = point.x;
                    }
                    // RIGHT
                    else if (point.x - treshold <= xNew + width && xNew + width <= point.x + treshold) {
                        res.x = ((point.x - width) * clientWidth) / 100;
                        locked.x = point.x - width;
                    }
                    // TOP
                    else if (point.y - treshold <= yNew && yNew <= point.y + treshold) {
                        res.y = (point.y * clientHeight) / 100;
                        locked.y = point.y;
                    }
                    // BOTTOM
                    else if (point.y - treshold <= yNew + height && yNew + height <= point.y + treshold) {
                        res.y = ((point.y - height) * clientHeight) / 100;
                        locked.y = point.y - height;
                    }
                    // CENTER X
                    else if (point.x - treshold <= xNew + width / 2 && xNew + width / 2 <= point.x + treshold) {
                        res.x = ((point.x - width / 2) * clientWidth) / 100;
                        locked.x = point.x - width / 2;
                    }
                    // CENTER Y
                    else if (point.y - treshold <= yNew + height / 2 && yNew + height / 2 <= point.y + treshold) {
                        res.y = ((point.y - height / 2) * clientHeight) / 100;
                        locked.y = point.y - height / 2;
                    }
                });
            }
            if (event.ctrlKey) {
                if (50 - treshold * 4 <= xNew + width / 2 && xNew + width / 2 <= 50 + treshold * 4) {
                    res.x = ((50 - width / 2) * clientWidth) / 100;
                }
                if (50 - treshold * 4 <= yNew + height / 2 && yNew + height / 2 <= 50 + treshold * 4) {
                    res.y = ((50 - height / 2) * clientHeight) / 100;
                }
            }
        });
        if (res.x || res.y) {
            treshold = 0.01;
        }
        Object.entries(points).forEach(([key, val]) => {
            if (!(+key === index) && event.altKey) {
                const pH = val[2].y - val[0].y;
                const pW = val[2].x - val[0].x;
                val.forEach((point: {x: number; y: number}, i: number) => {
                    // LEFT
                    if (locked.x && point.x - treshold <= locked.x && locked.x <= point.x + treshold) {
                        temp[0] = {
                            x1: (point.x * clientWidth) / 100 - 1,
                            x2: (point.x * clientWidth) / 100 - 1,
                            y1: ((point.y + pH / 2 - i * (pH / 2)) * clientHeight) / 100,
                            y2: (((locked.y ? locked.y : yNew) + height / 2) * clientHeight) / 100,
                        };
                    }
                    // RIGHT
                    if (locked.x && point.x - treshold <= locked.x + width && locked.x + width <= point.x + treshold) {
                        temp[1] = {
                            x1: (point.x * clientWidth) / 100 - 1,
                            x2: (point.x * clientWidth) / 100 - 1,
                            y1: ((point.y + pH / 2 - i * (pH / 2)) * clientHeight) / 100,
                            y2: (((locked.y ? locked.y : yNew) + height / 2) * clientHeight) / 100,
                        };
                    }
                    // TOP
                    if (locked.y && point.y - treshold <= locked.y && locked.y <= point.y + treshold) {
                        temp[2] = {
                            x1: ((point.x + pW / 2 - i * (pW / 2)) * clientWidth) / 100,
                            x2: ((locked.x ? locked.x : xNew) * clientWidth) / 100,
                            y1: (point.y * clientHeight) / 100 - 1,
                            y2: (point.y * clientHeight) / 100 - 1,
                        };
                    }
                    // BOTTOM
                    if (locked.y && point.y - treshold <= locked.y + height && locked.y + height <= point.y + treshold) {
                        temp[3] = {
                            x1: ((point.x + pW / 2 - i * (pW / 2)) * clientWidth) / 100,
                            x2: ((locked.x ? locked.x : xNew) * clientWidth) / 100,
                            y1: (point.y * clientHeight) / 100 - 1,
                            y2: (point.y * clientHeight) / 100 - 1,
                        };
                    }
                    // CENTER X
                    if (locked.x && point.x - treshold <= locked.x + width / 2 && locked.x + width / 2 <= point.x + treshold) {
                        temp[4] = {
                            x1: (point.x * clientWidth) / 100 - 1,
                            x2: (point.x * clientWidth) / 100 - 1,
                            y1: ((point.y + pH / 2 - i * (pH / 2)) * clientHeight) / 100,
                            y2: (((locked.y ? locked.y : yNew) + height / 2) * clientHeight) / 100,
                        };
                    }
                    // CENTER Y
                    if (locked.y && point.y - treshold <= locked.y + height / 2 && locked.y + height / 2 <= point.y + treshold) {
                        temp[5] = {
                            x1: ((point.x + pW / 2 - i * (pW / 2)) * clientWidth) / 100,
                            x2: ((locked.x ? locked.x : xNew) * clientWidth) / 100,
                            y1: (point.y * clientHeight) / 100 - 1,
                            y2: (point.y * clientHeight) / 100 - 1,
                        };
                    }
                    if (!res.x && !res.y) {
                        setter([]);
                    }
                    setter(temp.filter((item) => item));
                });
            }
            if (!(+key === index) && !event.altKey && !event.ctrlKey) {
                setter([]);
            }
        });
    }

    return res;
};
